import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axiosInstance from "../utils/axios";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { usePageSummary } from "../hooks/usePage";
import { usePageHeaderByKey } from "../hooks/usePageHeaderBykey";
import { useTeamMember } from "../hooks/useTeam";
import { HiOutlineMail } from "react-icons/hi";
import { FaPhoneAlt } from "react-icons/fa";
import PublicPageShell from "../components/PublicPageShell";
// Skeleton Loader for team members
const TeamSkeleton = () => (
  <div className="flex flex-col items-center text-center bg-white p-4 rounded-lg shadow-md w-full animate-pulse">
    <div className="w-24 h-24 rounded-full bg-gray-200 mb-4" />
    <div className="h-4 w-2/3 bg-gray-200 rounded mb-2" />
    <div className="h-3 w-1/2 bg-gray-200 rounded mb-2" />
    <div className="h-3 w-3/4 bg-gray-200 rounded mb-2" />
    <div className="h-3 w-1/3 bg-gray-200 rounded" />
  </div>
);

const drawerVariants = {
  hidden: { x: "100%" },
  visible: { x: 0, transition: { type: "spring", stiffness: 400, damping: 40 } },
  exit: { x: "100%", transition: { type: "spring", stiffness: 400, damping: 40 } },
};

const Team = () => {
  const {isLoading, pageHeader} = usePageHeaderByKey("team");
  const {isLoading:teamLoading, teamMembers} = useTeamMember()
  const [selectedMember, setSelectedMember] = useState(null);
  const [visible, setVisible] = useState(false);
  const { teamSummary } = usePageSummary();
  const [aboutBroker, setAboutBroker] = useState(null);

  useEffect(() => {
    axiosInstance.get("/api/about-broker")
      .then((res) => setAboutBroker(res.data))
      .catch(() => {});
  }, []);

  const openDialog = (member) => {
    setSelectedMember(member);
    setVisible(true);
  };

  return (
    <PublicPageShell>
      <div className="flex flex-col justify-center w-full">
        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="text-2xl mb-4 text-center font-bold text-black uppercase"
        >
          {pageHeader?.h1 || "AMERICAN REALTY NETWORK LLC TEAM"}
        </motion.h1>
        {(() => {
          const instr = (teamSummary?.instructionText || "").trim();
          const descPlain = (teamSummary?.description || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
          const tagline = instr || descPlain;
          if (!tagline) return null;
          return (
            <p className="mb-6 text-gray-600 text-center text-sm leading-relaxed">
              {instr ? instr : <span dangerouslySetInnerHTML={{ __html: teamSummary.description }} />}
            </p>
          );
        })()}

        {/* Team Members Grid */}
        <motion.div
          className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 w-full"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: { staggerChildren: 0.2 },
            },
          }}
        >
          {teamLoading || isLoading
            ? Array.from({ length: 6 }).map((_, i) => <TeamSkeleton key={i} />)
            : teamMembers?.map((member) => (
                <motion.div
                  key={member.id}
                  className="flex flex-col items-center text-center bg-white p-6 mb-4 rounded-lg shadow-md hover:shadow-xl transition-shadow w-full"
                  variants={{
                    hidden: { opacity: 0, y: 30 },
                    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
                  }}
                  whileHover={{ scale: 1.04 }}
                  onClick={() => openDialog(member)}
                >
                  <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full overflow-hidden border-4 border-white shadow-md mb-4 flex-shrink-0">
                    <motion.img
                      src={member.imageUrl}
                      alt={member.fullName}
                      className="w-full h-full object-cover cursor-pointer transition-transform duration-200"
                    />
                  </div>
                  <p className="mb-2 justify-center text-gray-700 md:text-sm leading-relaxed capitalize text-center break-words w-full font-bold">{member.fullName}</p>
                  {member.company && (
                    <p className="mb-2 text-gray-700 md:text-sm leading-relaxed text-center">{member.company}</p>
                  )}
                  <p className="mb-2 text-gray-700 md:text-sm leading-relaxed text-center break-all">{member.email}</p>
                  {member.phone && (
                    <p className="mb-2 text-gray-700 md:text-sm leading-relaxed text-center">{member.phone}</p>
                  )}
                </motion.div>
              ))}
        </motion.div>

        {/* About Broker Section - right after agent listings */}
        {aboutBroker && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="w-full mt-8 mb-6"
          >
            <h2 className="text-xl font-bold text-center text-black uppercase mb-4">
              {aboutBroker.title || "About Broker"}
            </h2>
            {aboutBroker.body && aboutBroker.body.trim() !== "" && (
              <div
                className="prose prose-sm max-w-none text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: aboutBroker.body }}
              />
            )}
          </motion.div>
        )}

        {/* Team Summary - only show if description has extra content not already shown under title */}
        {teamSummary?.description && (() => {
          const descPlain = (teamSummary.description || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
          const instr = (teamSummary.instructionText || "").trim();
          const taglineShown = instr || descPlain;
          if (!taglineShown || descPlain === taglineShown) return null; // Already shown under title
          return (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="w-full mb-4 text-gray-700 md:text-sm leading-relaxed"
            >
              <span dangerouslySetInnerHTML={{ __html: teamSummary.description }} />
            </motion.p>
          );
        })()}

        {/* Member Dialog */}
        <AnimatePresence>
          {selectedMember && (
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-auto p-6 pt-16 flex flex-col items-center"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              >
                {/* Close Button */}
                <button
                  className="absolute top-4 right-4 text-gray-400 hover:text-red-600 text-2xl"
                  onClick={() => setSelectedMember(null)}
                  aria-label="Close"
                >
                  &times;
                </button>
                {/* Profile Image - Overlapping */}
                <div className="absolute -top-14 left-1/2 transform -translate-x-1/2">
                  <div className="w-28 h-28 rounded-full border-4 border-white shadow-lg overflow-hidden">
                    <img
                      src={selectedMember.imageUrl}
                      alt={selectedMember.fullName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                {/* Content */}
                <div className="mt-5 w-full flex flex-col items-center">
                  <h2 className="text-lg font-bold text-gray-800 mb-4 text-center break-words">{selectedMember.fullName}</h2>
                  {selectedMember.company && (
                    <p className="text-gray-500 md:text-sm text-center">{selectedMember.company}</p>
                  )}
                  <div className="flex flex-col sm:flex-row justify-center gap-2 mt-2 w-full">
                    <span className="text-gray-600 text-center break-all">{selectedMember.email}</span>
                    {selectedMember.phone && (
                      <>
                        <span className="hidden sm:inline text-gray-400">|</span>
                        <span className="text-gray-600 text-center">{selectedMember.phone}</span>
                      </>
                    )}
                  </div>
                  <div className="w-full">
                    <h3 className="font-semibold mb-2 text-gray-700 text-center">
                      About {selectedMember.fullName.split(" ")[0]}
                    </h3>
                    <div className="text-gray-700 whitespace-pre-line max-h-48 overflow-y-auto border rounded p-4 bg-gray-50 shadow-inner text-sm">
                      {selectedMember.bio}
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </PublicPageShell>
  );
};

export default Team;
